import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import classNames from 'classnames';
import { StatusTip } from 'tea-component';
import { DefaultImg } from '@op/constants/defaultImg';
import RenderImg from '@op/components/RenderImg';
import { playableManager, generatePlayableId } from './playableManager';
import './index.scss';

export interface PlayableCoverProps {
  previewImg: string;
  type?: string[];
  className?: string;
}

/**
 * 可播放素材封面组件
 * 当 type 包含 'playable' 时使用 iframe 展示，否则使用 RenderImg
 *
 * 优化策略：
 * 1. React.memo：缓存组件，减少父组件变化导致的不必要重渲染
 * 2. 共享 Observer：全局共享一个 IntersectionObserver，避免创建过多实例
 * 3. 预加载：提前加载即将进入可视区域的 iframe（rootMargin 扩大观察范围）
 * 4. CSS 隐藏：被 LRU 淘汰的 iframe 用 CSS 隐藏而非移除，滚动回来时无需重新加载
 * 5. LRU 策略：限制同时可见的 iframe 数量，超出时隐藏最久未见的
 * 6. 加载节流：控制同时加载的请求数量，避免网络和渲染压力
 */
const PlayableCover: React.FC<PlayableCoverProps> = memo(
  ({
    previewImg,
    type,
    className,
  }) => {
    // isVisible: iframe 是否应该可见（由 manager 控制）
    // 一旦加载过的 iframe 会保留在 DOM 中，只是用 CSS 隐藏
    const [isVisible, setIsVisible] = useState(false);
    // hasLoaded: iframe 是否已经加载过（加载后保持 true，不再重置）
    const [hasLoaded, setHasLoaded] = useState(false);
    // loading: iframe 是否正在加载中
    const [loading, setLoading] = useState(true);

    const wrapperRef = useRef<HTMLDivElement>(null);
    // 使用 ref 存储 ID，避免组件重渲染时生成新 ID
    const idRef = useRef<string>(generatePlayableId());
    // 使用 ref 存储上一次的 previewImg，用于判断是否变化
    const prevPreviewImgRef = useRef<string>(previewImg);

    // 缓存 isPlayable 的计算结果
    const isPlayable = type?.includes('playable');

    // 用于 manager 控制 iframe 的显示/隐藏
    const handleSetVisible = useCallback((visible: boolean) => {
      setIsVisible(visible);
      // 只有首次加载时才设置 loading 状态
      // 已经加载过的 iframe 再次显示时不需要 loading
    }, []);

    // 处理 iframe 加载完成
    const handleIframeLoad = useCallback(() => {
      setLoading(false);
      setHasLoaded(true);
      // 通知 manager 加载完成，以便处理队列中的下一个
      playableManager.onLoadComplete(idRef.current);
    }, []);

    // 注册/注销 manager
    useEffect(() => {
      if (!isPlayable || !wrapperRef.current) return;

      const id = idRef.current;
      const element = wrapperRef.current;

      // 注册到 manager
      playableManager.register(id, previewImg, handleSetVisible, element);

      return () => {
        playableManager.unregister(id);
      };
    }, [isPlayable, handleSetVisible]);

    // 单独处理 previewImg 变化：更新已注册实例的 src
    useEffect(() => {
      if (!isPlayable) return;

      // 首次渲染或 previewImg 变化时
      if (prevPreviewImgRef.current !== previewImg) {
        prevPreviewImgRef.current = previewImg;
        // 重置状态
        setIsVisible(false);
        setHasLoaded(false);
        setLoading(true);
        // 更新 src 并重新加入队列
        playableManager.updateSrc(idRef.current, previewImg);
      }
    }, [isPlayable, previewImg]);

    if (isPlayable) {
      return (
        <div
          ref={wrapperRef}
          className={classNames('dbom-playable-cover-wrapper', className)}
        >
          {/* 显示 loading：仅首次加载中 */}
          {!hasLoaded && loading && (
            <div className="dbom-playable-cover-loading">
              <StatusTip status="loading" loadingText="" />
            </div>
          )}
          {/* iframe 始终渲染，通过 CSS 控制显示/隐藏 */}
          {/* 一旦加载过，即使隐藏也保留在 DOM 中，滚动回来时无需重新加载 */}
          {(isVisible || hasLoaded) && (
            <iframe
              src={previewImg}
              className={classNames('dbom-playable-cover', {
                'dbom-playable-cover--hidden': !isVisible || (!hasLoaded && loading),
              })}
              scrolling="no"
              frameBorder="0"
              onLoad={handleIframeLoad}
            />
          )}
        </div>
      );
    }

    return (
      <RenderImg
        coverImage={previewImg}
        defaultImage={DefaultImg.VIDEO}
        className={className}
      />
    );
  },
);

// 设置 displayName 方便调试
PlayableCover.displayName = 'PlayableCover';

export default PlayableCover;
